import { supabase } from '../../lib/supabaseClient';
import type {
  SecurityIncident,
  SecurityAlert,
  AuditSnapshot,
  RestoreEvent,
  PaypalEnvironmentSnapshot,
  IncidentSeverity,
  IncidentStatus,
  SnapshotType,
  RestoreStatus,
} from './models';

export async function fetchIncidents(filters?: {
  severity?: IncidentSeverity;
  status?: IncidentStatus;
}): Promise<SecurityIncident[]> {
  let query = supabase
    .from('security_incidents')
    .select('*')
    .order('created_at', { ascending: false });

  if (filters?.severity) {
    query = query.eq('severity', filters.severity);
  }
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data as SecurityIncident[];
}

export async function createIncident(
  data: Omit<SecurityIncident, 'id' | 'created_at' | 'updated_at'>
): Promise<SecurityIncident> {
  const { data: row, error } = await supabase
    .from('security_incidents')
    .insert(data)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return row as SecurityIncident;
}

export async function updateIncidentStatus(
  id: string,
  status: IncidentStatus,
  resolvedBy?: string
): Promise<void> {
  const updates: Record<string, unknown> = { status };
  if (status === 'resolved' || status === 'closed') {
    updates.resolved_at = new Date().toISOString();
    if (resolvedBy !== undefined) {
      updates.resolved_by = resolvedBy;
    }
  }
  const { error } = await supabase
    .from('security_incidents')
    .update(updates)
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function fetchAlerts(onlyActive?: boolean): Promise<SecurityAlert[]> {
  let query = supabase
    .from('security_alerts')
    .select('*')
    .order('created_at', { ascending: false });

  if (onlyActive) {
    query = query
      .is('acknowledged_at', null)
      .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data as SecurityAlert[];
}

export async function acknowledgeAlert(id: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('security_alerts')
    .update({ acknowledged_by: userId, acknowledged_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function createAlert(
  data: Omit<SecurityAlert, 'id' | 'created_at'>
): Promise<SecurityAlert> {
  const { data: row, error } = await supabase
    .from('security_alerts')
    .insert(data)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return row as SecurityAlert;
}

export async function fetchSnapshots(type?: SnapshotType): Promise<AuditSnapshot[]> {
  let query = supabase
    .from('audit_snapshots')
    .select('*')
    .order('created_at', { ascending: false });

  if (type) {
    query = query.eq('snapshot_type', type);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data as AuditSnapshot[];
}

export async function createSnapshot(
  data: Omit<AuditSnapshot, 'id' | 'created_at'>
): Promise<AuditSnapshot> {
  const { data: row, error } = await supabase
    .from('audit_snapshots')
    .insert(data)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return row as AuditSnapshot;
}

export async function fetchRestoreEvents(): Promise<RestoreEvent[]> {
  const { data, error } = await supabase
    .from('restore_events')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data as RestoreEvent[];
}

export async function createRestoreEvent(
  data: Omit<RestoreEvent, 'id' | 'created_at'>
): Promise<RestoreEvent> {
  const { data: row, error } = await supabase
    .from('restore_events')
    .insert(data)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return row as RestoreEvent;
}

export async function updateRestoreEventStatus(
  id: string,
  status: RestoreStatus,
  integrityCheck?: Record<string, unknown>
): Promise<void> {
  const updates: Record<string, unknown> = { status };
  if (status === 'completed' || status === 'failed') {
    updates.completed_at = new Date().toISOString();
  }
  if (integrityCheck !== undefined) {
    updates.post_restore_integrity_check = integrityCheck;
  }
  const { error } = await supabase
    .from('restore_events')
    .update(updates)
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function fetchPaypalSnapshots(): Promise<PaypalEnvironmentSnapshot[]> {
  const { data, error } = await supabase
    .from('paypal_environment_snapshots')
    .select('*')
    .order('captured_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data as PaypalEnvironmentSnapshot[];
}
